// =============================================================================
// supplier-dashboard-client.ts — Client API du TABLEAU DE BORD FOURNISSEUR (DBU §3.2, Lot A / D3b).
// =============================================================================
// Miroir HTTP de l'endpoint FOURNISSEUR `GET /supplier/dashboard`
// (apps/api/src/rate-collection/supplier-dashboard.controller.ts), session opaque sous
// withTenant(supplier_siret). LECTURE PURE : aucune mutation, aucune règle métier ici (autorité = API :
// siret de la session, partition/tri/complétion calculés serveur). AUCUNE donnée métier en localStorage
// (RULE_REF_006). Horodatages = ISO 8601 (string), jamais d'objet Date ORM.
//
// DTO = miroirs HTTP ASCII (parité DTO API supplier-dashboard.dto.ts) : les enums sont répliqués en unions
// de littéraux (parité Prisma CampaignState/SubmissionState). La SPA ne couple PAS au paquet DB.

/** Phase opérationnelle dérivée serveur (DBU §3.2) — pilote libellé + action, jamais une couleur seule. */
export type DashboardPhaseDto = 'Ouverture' | 'Analyse' | 'Negociation' | 'Cloture';

/**
 * État de campagne (parité Prisma CampaignState) — projeté en string par l'API. Le front l'affiche via un
 * libellé FR (jamais l'enum brut) ; sert aussi de renfort textuel au badge de statut (WCAG, sens doublé).
 */
export type CampaignStateDto =
  | 'Brouillon'
  | 'Planifiee'
  | 'OuverteC1'
  | 'AnalyseC1'
  | 'OuverteC2'
  | 'AnalyseC2'
  | 'Negociation'
  | 'Publiee'
  | 'Abandonnee';

/** État d'une submission (parité Prisma SubmissionState). */
export type SubmissionStateDto =
  | 'Vide'
  | 'Brouillon'
  | 'Soumis'
  | 'Verrouille'
  | 'ReModifiable';

/**
 * Taux de complétion d'une submission (DBU §3.2 « Progression », ex. « 42 / 82 profils saisis »).
 * `profilesSaisis` = profils avec ≥ 1 cellule non vide ; `profilesRequis` = profils du périmètre figé.
 */
export interface DashboardCompletionDto {
  readonly profilesSaisis: number;
  readonly profilesRequis: number;
  readonly cellsSaisies: number;
  readonly cellsRequises: number;
}

/** Une participation du fournisseur à une campagne (dernière réponse = cycle le plus élevé). */
export interface DashboardParticipationDto {
  readonly campaignId: string;
  readonly submissionId: string;
  readonly intitule: string;
  readonly campaignState: CampaignStateDto;
  readonly submissionState: SubmissionStateDto;
  readonly phase: DashboardPhaseDto;
  readonly cycleNumber: number;
  /** Date de lancement (openAt du cycle 1, ISO 8601) ou `null`. */
  readonly launchAt: string | null;
  /** Échéance du cycle courant (close_at, ISO 8601) ou `null` — alimente le compte à rebours. */
  readonly cycleCloseAt: string | null;
  readonly completion: DashboardCompletionDto;
}

/**
 * Tableau de bord fournisseur (DBU §3.2). `active` = sollicitations en cours (triées par échéance
 * croissante, la plus urgente en tête pour Focus Campagne) ; `past` = historique (Publiée/Abandonnée).
 */
export interface SupplierDashboardDto {
  readonly generatedAt: string;
  readonly active: readonly DashboardParticipationDto[];
  readonly past: readonly DashboardParticipationDto[];
}

/** Genre d'erreur applicative du dashboard fournisseur, mappé pour un message UX explicite. */
export type SupplierDashboardApiErrorKind =
  | 'unauthorized'
  | 'forbidden'
  | 'not-found'
  | 'generic';

/**
 * Erreur applicative renvoyée par l'API dashboard (HTTP 4xx/5xx). L'UI l'affiche telle quelle ; elle ne
 * réinterprète JAMAIS la règle métier (autorité = API). Le `kind` permet un libellé UX ciblé.
 */
export class SupplierDashboardApiError extends Error {
  public readonly kind: SupplierDashboardApiErrorKind;

  public constructor(
    message: string,
    kind: SupplierDashboardApiErrorKind = 'generic',
  ) {
    super(message);
    this.name = 'SupplierDashboardApiError';
    this.kind = kind;
  }
}

/**
 * Contrat du client dashboard FOURNISSEUR — miroir de l'endpoint D3b. LECTURE PURE : le siret vient de la
 * session (jamais du body) ; la partition/tri/complétion sont l'autorité de l'API. L'UI relaie l'affichage.
 */
export interface SupplierDashboardClient {
  /** GET tableau de bord du fournisseur en session (participations actives + historique + complétion). */
  getDashboard(): Promise<SupplierDashboardDto>;
}
